import {
  ApacHotelsComponent,
  useApacHotelsStyles,
} from "@apac/components/hotels";
import {
  type GuestCounts,
  useHotelDateRange,
  useHotelGuestCount,
} from "@apac/hooks";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import type { FiatPrice, PlatformComponentProps } from "@b2bportal/core-types";
import type { Prices } from "@b2bportal/lodging-api";
import { LodgingSelectors } from "@checkout/states";
import { useCheckoutStateSelector } from "@hopper-b2b/checkout";
import { useI18nContext, useI18nList } from "@hopper-b2b/i18n";
import { Icon, Popover, PopoverContent, PopoverTrigger } from "@tenant/ui";
import clsx from "clsx";
import { FintechPriceBreakdownSection } from "../FintechPriceBreakdownSection";
import {
  getRbcLodgingPriceBreakdown,
  type RbcLodgingPriceBreakdown,
} from "../PayAtProperty/getRbcLodgingPriceBreakdown";
import defaultStyles from "./styles.module.scss";

/**
 * Pre-computed pay-now taxes & fees (never derived on the frontend).
 * Quote (`PricingV2`) exposes `taxesAndFeesDueNow`; shop (`CustomerPriceBreakdown`)
 * exposes the `stayTaxesAndFees` union (DueNow member for pay-now rates).
 */
const getTaxesDueNow = (
  breakdown: RbcLodgingPriceBreakdown | undefined
): Prices | undefined => {
  if (breakdown == null) {
    return undefined;
  }
  if ("taxesAndFeesDueNow" in breakdown) {
    return breakdown.taxesAndFeesDueNow.total;
  }
  const stayTaxesAndFees = breakdown.stayTaxesAndFees;
  return stayTaxesAndFees?.StayTaxAndFeeDetails === "StayTaxAndFeeDetailsDueNow"
    ? stayTaxesAndFees.total
    : undefined;
};

export interface BasePriceBreakdownProps extends PlatformComponentProps {
  separator?: string;
  showBedAndGuests?: boolean;
  showRoomInfo?: boolean;
  showPayLaterBreakdown?: boolean;
  currencyDifference?: FiatPrice;
  guestCounts?: GuestCounts;
  fiatFormatOptions?: Intl.NumberFormatOptions;
}

const COMPONENT_KEY = ApacHotelsComponent.BasePriceBreakdown;

export const BasePriceBreakdown = ({
  className,
  separator = "x",
  showBedAndGuests = false,
  showRoomInfo = false,
  currencyDifference,
  guestCounts,
  fiatFormatOptions,
}: BasePriceBreakdownProps) => {
  const styles = useApacHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { t, formatFiatCurrency } = useI18nContext();
  const { formatList } = useI18nList();
  const { dateFormattedString } = useHotelDateRange({ useShopData: true });

  const breakdown = useCheckoutStateSelector(getRbcLodgingPriceBreakdown);

  const shopProduct = useCheckoutStateSelector(
    LodgingSelectors.getSelectedRoomProduct
  );
  const roomsCount =
    useCheckoutStateSelector(LodgingSelectors.getSelectedRoomsCount) ?? 1;
  const stayNights = useCheckoutStateSelector(LodgingSelectors.getTotalNights);

  const taxesDueNow = getTaxesDueNow(breakdown);
  const hasTaxesDueNow = (taxesDueNow?.fiat?.value ?? 0) > 0;

  const { formattedGuestCountString } = useHotelGuestCount(guestCounts);
  const totalNightsTranslated = t("nights", {
    count: stayNights,
  });
  const roomsString = t("recentSearch.hotels.rooms", {
    count: roomsCount,
  });
  const infoStrings = [totalNightsTranslated];
  infoStrings.push(roomsString);
  if (showBedAndGuests) {
    infoStrings.push(formattedGuestCountString());
  }

  const bedLabel = formatList(
    shopProduct?.bedTypes?.rooms?.map((room) => room.bedTypes.description) ??
      [],
    { type: "conjunction" }
  );

  return (
    <div className={clsx(block, className)}>
      <div className={cn("line-container")}>
        <div className={cn("base-price-label")}>
          <span className={cn("passengers-info")}>
            {infoStrings.join(separator)}
          </span>

          {showRoomInfo && (
            <span className={cn("room-info")}>{shopProduct?.roomName}</span>
          )}
          {showBedAndGuests && (
            <span className={cn("bed-info")}>{bedLabel}</span>
          )}
          <span className={cn("date-info")}>({dateFormattedString})</span>
        </div>

        <div className={cn("summary-item-value")}>
          {formatFiatCurrency(
            breakdown?.roomRateTotal?.fiat,
            fiatFormatOptions
          )}
        </div>
      </div>

      {hasTaxesDueNow && (
        <div className={cn("line-container")}>
          <Popover>
            <PopoverTrigger>
              <div className={cn("summary-item-label")}>
                {t("apac.taxesAndServiceCharges")}
                <Icon
                  className="taxes-popover-fees-icon"
                  iconName={IconNameEnum.ui__infoPopover}
                />
              </div>
            </PopoverTrigger>
            <PopoverContent
              className={cn("taxes-popover-content")}
              showCloseButton={false}
            >
              {t("core-checkout.priceBreakdown.taxesAndFeesTooltip")}
            </PopoverContent>
          </Popover>
          <div className={cn("summary-item-value")}>
            {formatFiatCurrency(taxesDueNow?.fiat, fiatFormatOptions)}
          </div>
        </div>
      )}

      <FintechPriceBreakdownSection />

      {currencyDifference != null && (
        <div className={cn("line-container")}>
          <Popover>
            <PopoverTrigger>
              <div className={cn("summary-item-label")}>
                {t("apac.checkout.review.currencyConversion")}
                <Icon
                  className="taxes-popover-fees-icon"
                  iconName={IconNameEnum.ui__infoPopover}
                />
              </div>
            </PopoverTrigger>
            <PopoverContent
              className={cn("taxes-popover-content")}
              showCloseButton={false}
            >
              {t(
                "apac.checkout.review.currencyConversionDisclaimerDescription"
              )}
            </PopoverContent>
          </Popover>
          <div className={cn("summary-item-value")}>
            {formatFiatCurrency(currencyDifference, fiatFormatOptions)}
          </div>
        </div>
      )}
    </div>
  );
};
